/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useState } from 'react'
import { useForm, FormProvider, type SubmitHandler } from 'react-hook-form'
import { useAppDispatch } from '../../../store'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Grid from '@mui/material/Grid'
import FormInput from '../../../components/FormElements/FormInput'
import ValidationAlert from '../../../components/ValidationAlert'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { createDivisionCoursesApi, getDivisionCoursesApi, updateDivisionCoursesApi } from '../../../store/slices/courseCatalogSlice/api'
import { actionLoading } from '../../../store/slices/courseCatalogSlice/selectors'
import { ApiAutoComplete } from '../../../components/FormElements/ApiAutoComplete'
import AttributesMultiSelect from '../../../components/FormElements/AttributesMultiSelect'
import { type IAttribute } from '../../../models/TermsSection'
import { type IDivisionRow } from '../../../models/Division'
import { type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import { bindSetError, showServerFieldErrors } from '../../../utils/showServerFieldErrors'
import { ATTRIBUTE_TYPES, READONLY_TOOLTIP } from '../../../consts'
import { LastUpdateMeta } from '../../../components/GenericModal/ChangeMetaInfo'
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useAccess } from '../../../hooks/useAccess'
import { grey } from '../../../colors'

interface CreateAndEditCourseCatalogProps {
  selectedRow?: IDivisionRow
  selectedRowWithActions?: any
  handleCloseModal?: () => void
  params?: ApplyFiltersAndDispatchParams
  isTaskRunning?: boolean
  onTaskBlock?: () => void
}

interface FormType {
  title: string
  abbreviation: string
  categoryId?: string
  parent?: string
  attributes?: Array<Record<string, any>>
  integrationSourceId?: string
  institutionId?: string
}

interface UpdatePayload {
  title: string
  attributes: Array<Record<string, any>> | undefined
  parent?: string
}

const courseDivisionSchema = yup.object().shape({
  title: yup.string().required('Title is required'),
  abbreviation: yup.string().required('Course ID is required'),
  categoryId: yup.string(),
  parent: yup.string(),
  attributes: yup.array()
})

const CreateAndEditCourseCatalog: React.FC<CreateAndEditCourseCatalogProps> = ({ handleCloseModal, selectedRow, params, selectedRowWithActions, isTaskRunning, onTaskBlock }) => {
  const { readonly } = useAccess(selectedRowWithActions?.actions ?? [])
  const [errors, setErrors] = useState<unknown>({})
  const isEditAction = selectedRow !== undefined
  const dispatch = useAppDispatch()
  const loading = actionLoading()

  useEffect(() => {
    if (selectedRow != null && isTaskRunning) {
      onTaskBlock?.()
      handleCloseModal?.()
    }
  }, [selectedRow])

  const methods = useForm<FormType>({
    resolver: yupResolver(courseDivisionSchema),
    defaultValues: {
      title: selectedRow?.title,
      abbreviation: selectedRow?.abbreviation ?? '',
      categoryId: 'course',
      parent: selectedRow?.parent?.divisionId,
      attributes: selectedRow?.attributes,
      integrationSourceId: selectedRow?.integrationSourceId ?? undefined,
      institutionId: selectedRow?.institutionId ?? undefined
    }
  })

  const { handleSubmit, formState: { isDirty, isSubmitting }, control } = methods

  const isParentLocked = !!(isEditAction && selectedRow?.parent?.divisionId)

  const onSubmit: SubmitHandler<FormType> = async ({ title, abbreviation, categoryId, parent, attributes, institutionId, integrationSourceId }) => {
    try {
      const normalizedParent = parent != null && String(parent).trim() !== '' ? parent : undefined
      const hasParentUpdated = selectedRow?.parent?.divisionId !== normalizedParent
      const updatePayload: UpdatePayload & { institutionId?: string, integrationSourceId?: string } = { title, attributes }

      if (hasParentUpdated) {
        updatePayload.parent = normalizedParent
      }
      if (institutionId) {
        updatePayload.institutionId = institutionId
      }
      if (integrationSourceId) {
        updatePayload.integrationSourceId = integrationSourceId
      }

      const apiCall = isEditAction
        ? dispatch(updateDivisionCoursesApi({ urlId: selectedRow?.divisionId, data: updatePayload })).unwrap()
        : dispatch(createDivisionCoursesApi({ data: { title, categoryId, parent: normalizedParent, abbreviation, attributes, institutionId, integrationSourceId } })).unwrap()

      await apiCall
      await dispatch(getDivisionCoursesApi({ params }))
      handleCloseModal?.()
    } catch (error) {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    }
  }

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <FormInput name="title" label="Title" size="medium" required />
          </Grid>
          <Grid item xs={12}>
            <FormInput name="abbreviation" label="Course ID" size="medium" required disabled={isEditAction} />
          </Grid>
          <Grid item xs={12}>
            <ApiAutoComplete
              apiEndpoint="/api/divisions/allowed_course_parents"
              label="Parent Unit (Optional)"
              placeholder="Search for parent unit"
              name="parent"
              valueField="divisionId"
              labelField="title"
              selectedItem={selectedRow?.parent}
              readOnly={isParentLocked}
              getOptionSubLabel={(item: any) => item.abbreviation}
              clearValue=""
              skip={readonly}
            />
          </Grid>

          <Grid item xs={12}>
            <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Additional Fields</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box display='flex' flexDirection='column' gap={4}>
                  <AttributesMultiSelect
                    extraParams={{ attributeType: ATTRIBUTE_TYPES.COURSE_ATTRIBUTES }}
                    defaultAttributes={selectedRow?.attributes as IAttribute[]}
                    required
                    name="attributes"
                    skip={readonly}
                  />
                  <Box>
                    <FormInput name="institutionId" defaultValue={selectedRow?.institutionId ?? undefined} label="Institution ID" control={control} placeholder='Institution ID' />
                    <Typography fontSize="14px" mt={1} color={grey[850]}>
                      Identifier from your SIS or data warehouse. Changing this may break syncing with your institutional data.
                    </Typography>
                  </Box>
                  <Box>
                    <FormInput name="integrationSourceId" defaultValue={selectedRow?.integrationSourceId ?? undefined} label="Integration Source ID" control={control} placeholder='Integration Source ID' />
                    <Typography fontSize="14px" mt={1} color={grey[850]}>
                      Identifier from an external system (e.g. LMS). Changing this may break the connection to that system and any related functionality.
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>

        </Grid>
        <DefaultModalActions
          destructiveButtonProps={{ onClick: handleCloseModal }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            loading,
            type: 'submit',
            disabled: readonly || isSubmitting || (isEditAction && !isDirty),
            tooltip: readonly ? READONLY_TOOLTIP : undefined
          }}
          lastUpdateMeta={isEditAction && (
            <LastUpdateMeta lastEditedBy={selectedRow?.modifiedByUsername} lastEditedAt={selectedRow?.modifiedOn} />
          )}

        />
      </form>
    </FormProvider>
  )
}

export default CreateAndEditCourseCatalog
